// #292 — URL→exam reconciliation helpers.
//
// The app keeps three mirrors of "selected exam" (users.exam on the server,
// localStorage.selectedmenu on the client, and the header dropdown). Page data is
// resolved from the persisted selection, NOT from the URL, so opening/reloading a URL
// for a different exam loads the wrong exam's data (or crashes in examWiseModelProvider).
//
// This module derives the exam *family* from the URL so the layout guard (containers/Full)
// can reconcile the selection to match the opening URL. See
// docs/superpowers/specs/2026-06-24-292-url-exam-reconciliation-design.md.

import {
  examNameAccordingToDB,
  CFAExamList,
  FRMExamList,
  ActuarialExamList,
  GMATExamList,
} from './constant';

// Stable, exam-agnostic family identifiers used internally by the guard.
export const EXAM_FAMILY = {
  CFA1: 'cfa1',
  CFA2: 'cfa2',
  CFA3: 'cfa3',
  FRM: 'frm',
  ESG: 'esg',
  SOA_P: 'soa_p',
  SOA_FM: 'soa_fm',
  GMAT_FOCUS: 'gmat_focus',
  EA: 'ea',
};

// Every concrete exam code (the value stored in localStorage.selectedmenu / users.exam)
// mapped to its family. Covers legacy and year-suffixed CFA codes.
const CODE_TO_FAMILY = {
  [examNameAccordingToDB.CFA]: EXAM_FAMILY.CFA1,
  [examNameAccordingToDB.CFA_LEVEL_1_2024]: EXAM_FAMILY.CFA1,
  [examNameAccordingToDB.CFA_LEVEL_1_2025]: EXAM_FAMILY.CFA1,
  [examNameAccordingToDB.CFA_LEVEL_1_2026]: EXAM_FAMILY.CFA1,
  [examNameAccordingToDB.CFA_LEVEL_1_2027]: EXAM_FAMILY.CFA1,
  [examNameAccordingToDB.CFA_2]: EXAM_FAMILY.CFA2,
  [examNameAccordingToDB.CFA_LEVEL_2]: EXAM_FAMILY.CFA2,
  [examNameAccordingToDB.CFA_LEVEL_2_2025]: EXAM_FAMILY.CFA2,
  [examNameAccordingToDB.CFA_LEVEL_2_2026]: EXAM_FAMILY.CFA2,
  [examNameAccordingToDB.CFA_LEVEL_2_2027]: EXAM_FAMILY.CFA2,
  [examNameAccordingToDB.CFA_LEVEL_3]: EXAM_FAMILY.CFA3,
  [examNameAccordingToDB.CFA_LEVEL_3_2025]: EXAM_FAMILY.CFA3,
  [examNameAccordingToDB.CFA_LEVEL_3_2026]: EXAM_FAMILY.CFA3,
  [examNameAccordingToDB.ESG]: EXAM_FAMILY.ESG,
  [examNameAccordingToDB.FRM]: EXAM_FAMILY.FRM,
  [examNameAccordingToDB.SOA_P]: EXAM_FAMILY.SOA_P,
  [examNameAccordingToDB.SOA_FM]: EXAM_FAMILY.SOA_FM,
  [examNameAccordingToDB.GMAT]: EXAM_FAMILY.GMAT_FOCUS,
  [examNameAccordingToDB.GMAT_FOCUS]: EXAM_FAMILY.GMAT_FOCUS,
  [examNameAccordingToDB.EA]: EXAM_FAMILY.EA,
};

// Ordered URL-prefix → family table. Positive matches only: any path that matches none of
// these returns null = "no switch" (covers account/payment routes and the genuinely
// ambiguous shared `/videos` route, which both cfa2 and cfa3 use).
const URL_PREFIX_RULES = [
  { family: EXAM_FAMILY.CFA1, prefixes: ['/cfa1'] },
  { family: EXAM_FAMILY.CFA2, prefixes: ['/cfa2'] },
  { family: EXAM_FAMILY.CFA3, prefixes: ['/cfa3', '/pathway'] },
  { family: EXAM_FAMILY.SOA_P, prefixes: ['/soa/probability'] },
  { family: EXAM_FAMILY.SOA_FM, prefixes: ['/soa/financialmathematics'] },
  { family: EXAM_FAMILY.FRM, prefixes: ['/frm'] },
  { family: EXAM_FAMILY.ESG, prefixes: ['/esg'] },
  { family: EXAM_FAMILY.GMAT_FOCUS, prefixes: ['/gmat'] },
  { family: EXAM_FAMILY.EA, prefixes: ['/ea', '/EATab'] },
];

// Shared content routes that legitimately belong to more than one exam family. The URL
// alone can't identify the exam, so: if the current selection is already one of the
// allowed families, keep it (the page renders that exam's content); otherwise switch to
// `defaultFamily`. `/videos` (and `/videos/list/...`) is shared by cfa2 and cfa3 — a user
// on any other exam (e.g. esg) would otherwise get an empty page, so default to cfa2.
const SHARED_ROUTE_RULES = [
  {
    prefixes: ['/videos'],
    allowedFamilies: [EXAM_FAMILY.CFA2, EXAM_FAMILY.CFA3],
    defaultFamily: EXAM_FAMILY.CFA2,
  },
];

const startsWith = (path, prefix) => path.indexOf(prefix) === 0;

// Derive the exam family from a router pathname, or null when the URL carries no single
// exam (shared/account/payment routes). Note `/soa/...` is checked before `/frm`/`/esg`
// etc. only matters within its own family; order here is specific-first for safety.
export const getExamFamilyFromPath = (pathname) => {
  if (!pathname) {
    return null;
  }
  let i = 0;
  for (i = 0; i < URL_PREFIX_RULES.length; i++) {
    const rule = URL_PREFIX_RULES[i];
    let j = 0;
    for (j = 0; j < rule.prefixes.length; j++) {
      if (startsWith(pathname, rule.prefixes[j])) {
        return rule.family;
      }
    }
  }
  return null;
};

// Resolve the exam family a path should be viewed under, given the current selection.
// Returns a single-exam family directly; for shared routes (e.g. /videos) it keeps the
// current family when it's allowed, else falls back to the route's default; null otherwise.
export const resolveExamFamilyForPath = (pathname, currentFamily) => {
  const direct = getExamFamilyFromPath(pathname);
  if (direct) {
    return direct;
  }
  if (!pathname) {
    return null;
  }
  let i = 0;
  for (i = 0; i < SHARED_ROUTE_RULES.length; i++) {
    const rule = SHARED_ROUTE_RULES[i];
    let j = 0;
    for (j = 0; j < rule.prefixes.length; j++) {
      if (startsWith(pathname, rule.prefixes[j])) {
        if (currentFamily && rule.allowedFamilies.indexOf(currentFamily) !== -1) {
          return currentFamily;
        }
        return rule.defaultFamily;
      }
    }
  }
  return null;
};

// CFA1 2027 lives at the year-free `/cfa1/2027/...` tree. It shares the CFA1 family prefix, so
// the family-level reconciliation above can't tell it apart from 2025/2026 — a `/next/` deep-link
// (e.g. from Bedrock) opened by a 2025/2026 user would resolve to the CFA1 default code and render
// the wrong year. So `/next/` needs a CODE-level override that forces `cfa_2027` specifically.
const NEXT_PREFIX = '/cfa1/2027';

export const isNextPath = (pathname) =>
  !!pathname && startsWith(pathname, NEXT_PREFIX);

// CFA2 2027 mirrors CFA1 2027: it lives at /cfa2/2027/... inside the shared CFA2 family prefix, so it
// needs a CODE-level override to force cfa_2_2027 (family reconciliation alone can't tell it from the
// 2026 tree at /cfa2/v2/... or the bare /cfa2/... CBT routes).
const NEXT_PREFIX_CFA2 = '/cfa2/2027';

export const isNextPathCfa2 = (pathname) =>
  !!pathname && startsWith(pathname, NEXT_PREFIX_CFA2);

// Resolve the concrete exam CODE a path should be reconciled to, given the current code, or null
// for "no switch". `/cfa1/2027/...` forces `cfa_2027`. Every other path defers to the existing
// family-level rules AND preserves their "don't switch within the same family" behaviour (so a
// 2026 user opening a shared `/cfa1/...` URL is NOT bumped to the CFA1 default). This is the single
// entry point the layout guard uses.
export const resolveExamCodeForPath = (pathname, currentCode) => {
  // /cfa1/2027/... always forces cfa_2027 (up-switch if the session isn't already 2027).
  if (isNextPath(pathname)) {
    return examNameAccordingToDB.CFA_LEVEL_1_2027;
  }
  // /cfa2/2027/... always forces cfa_2_2027.
  if (isNextPathCfa2(pathname)) {
    return examNameAccordingToDB.CFA_LEVEL_2_2027;
  }
  const currentFamily = getFamilyFromExamCode(currentCode);
  const targetFamily = resolveExamFamilyForPath(pathname, currentFamily);
  if (!targetFamily) {
    return null;
  }
  // Force-down: a non-2027 CFA1 URL (e.g. /cfa1/2026/...) must NOT keep rendering 2027. Reconcile
  // the 2027 session down to the shared CFA1 default (cfa_2025 = live 2026 content), so 2027 is
  // reachable ONLY under /cfa1/2027/.... Other CFA1 years on shared URLs fall through to the
  // same-family "no switch" below.
  if (
    targetFamily === EXAM_FAMILY.CFA1 &&
    currentCode === examNameAccordingToDB.CFA_LEVEL_1_2027
  ) {
    return getExamCodeForFamily(EXAM_FAMILY.CFA1);
  }
  // Force-down: a non-2027 CFA2 *URL under /cfa2* (/cfa2/v2/..., bare /cfa2/... CBT) must NOT keep
  // rendering 2027 — reconcile the cfa_2_2027 session down to the CFA2 default (cfa_2_2025 = live 2026),
  // so 2027 is reachable ONLY under /cfa2/2027/.... The `startsWith('/cfa2')` guard is essential: the
  // CFA2 videos live on the SHARED /videos page (family resolves to CFA2 there too), and without the
  // guard a 2027 user opening /videos would be wrongly bumped to 2025. /videos keeps the 2027 token.
  if (
    targetFamily === EXAM_FAMILY.CFA2 &&
    currentCode === examNameAccordingToDB.CFA_LEVEL_2_2027 &&
    startsWith(pathname, '/cfa2')
  ) {
    return getExamCodeForFamily(EXAM_FAMILY.CFA2);
  }
  if (targetFamily === currentFamily) {
    return null;
  }
  return getExamCodeForFamily(targetFamily);
};

// Family of a stored exam code (localStorage.selectedmenu / users.exam), or null.
export const getFamilyFromExamCode = (examCode) => {
  if (!examCode) {
    return null;
  }
  return CODE_TO_FAMILY[examCode] || null;
};

// Reuse the header-dropdown lists as the single source of truth for which concrete
// year-code a family resolves to. Taking the first list entry per family yields exactly
// the value a manual dropdown click would assign (e.g. cfa1 -> cfa_2025), and adapts
// automatically when the lists change (e.g. the future 2026/2027 -> single CFA1 collapse).
export const getExamCodeForFamily = (family) => {
  if (!family) {
    return null;
  }
  const allSelectable = []
    .concat(CFAExamList())
    .concat(FRMExamList)
    .concat(ActuarialExamList)
    .concat(GMATExamList);
  let i = 0;
  for (i = 0; i < allSelectable.length; i++) {
    const option = allSelectable[i];
    if (option && getFamilyFromExamCode(option.value) === family) {
      return option.value;
    }
  }
  return null;
};



// WEBPACK FOOTER //
// ./src/common/examUrlResolver.js